// apiClient.ts
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { getCookieValue } from '../tokenUtils';
import { apiEventEmitter } from './apiEventEmitter';
import { ApiError, ApiResponse } from './apiTypes';

type ApiResult<T> = T & { status: number };
const createApiClient = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: process.env.REACT_APP_ADMIN_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
    },
    timeout: 10000,
  });

  // Track active requests by endpoint
  const activeRequests = new Map<string, number>();

  const getEndpointKey = (config: AxiosRequestConfig) => {
    return `${config.method?.toUpperCase()}:${config.url}`;
  };

  const updateLoadingState = (
    config: AxiosRequestConfig,
    increment: number
  ) => {
    if (!config.url) return;

    const key = getEndpointKey(config);
    const currentCount = activeRequests.get(key) || 0;
    const newCount = Math.max(0, currentCount + increment);

    if (newCount === 0) {
      activeRequests.delete(key);
    } else {
      activeRequests.set(key, newCount);
    }

    apiEventEmitter.emit({
      isLoading: newCount > 0,
      url: config.url,
      method: config.method?.toUpperCase(),
    });

    // Emit global loading state if needed
    const globalLoading = Array.from(activeRequests.values()).some(
      count => count > 0
    );
    apiEventEmitter.emit({ isLoading: globalLoading });
  };

  // Request interceptor
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      updateLoadingState(config, 1);

      const token = getCookieValue('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Request:', {
          url: config.url,
          method: config.method,
          headers: config.headers,
          data: config.data,
        });
      }

      return config;
    },
    (error: AxiosError) => {
      if (error.config) {
        updateLoadingState(error.config, -1);
      }

      if (process.env.NODE_ENV === 'development') {
        console.error('Request Error:', error);
      }
      return Promise.reject(error);
    }
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      updateLoadingState(response.config, -1);

      if (process.env.NODE_ENV === 'development') {
        console.log('Response:', {
          url: response.config.url,
          status: response.status,
          data: response.data,
        });
      }
      return response;
    },
    (error: AxiosError<ApiResponse>) => {
      if (error.config) {
        updateLoadingState(error.config, -1);
      }

      if (process.env.NODE_ENV === 'development') {
        console.error('Response Error:', error);
      }

      if (error.response) {
        const { status, data } = error.response;
        const errorMessage = data?.message || error.message;
        return Promise.reject(new ApiError(errorMessage, status, data));
      } else if (error.request) {
        return Promise.reject(new ApiError('No response received from server'));
      } else {
        return Promise.reject(
          new ApiError(`Request setup error: ${error.message}`)
        );
      }
    }
  );

  return instance;
};

const axiosInstance = createApiClient();

// API client object with methods
const api = {
  /**
   * Make a GET request
   * @param url The endpoint URL
   * @param config Optional axios config
   * @returns Promise with typed response data
   */
  get: async <T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await axiosInstance.get<ApiResponse<T>>(url, config);
    return response.data;
  },

  /**
   * Make a POST request
   * @param url The endpoint URL
   * @param data The request payload
   * @param config Optional axios config
   * @returns Promise with typed response data
   */
  post: async <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResult<T>> => {
    const response = await axiosInstance.post<ApiResponse<T>>(
      url,
      data,
      config
    );
    return {
      ...(response.data as T),
      status: response.status,
    };
  },

  /**
   * Make a PUT request
   * @param url The endpoint URL
   * @param data The request payload
   * @param config Optional axios config
   * @returns Promise with typed response data
   */
  put: async <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await axiosInstance.put<ApiResponse<T>>(url, data, config);
    return response.data;
  },

  /**
   * Make a DELETE request
   * @param url The endpoint URL
   * @param config Optional axios config
   * @returns Promise with typed response data
   */
  delete: async <T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await axiosInstance.delete<ApiResponse<T>>(url, config);
    return response.data;
  },

  /**
   * Make a PATCH request
   * @param url The endpoint URL
   * @param data The request payload
   * @param config Optional axios config
   * @returns Promise with typed response data
   */
  patch: async <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await axiosInstance.patch<ApiResponse<T>>(
      url,
      data,
      config
    );
    return response.data;
  },

  /**
   * Access the underlying axios instance for advanced use cases
   */
  axios: axiosInstance,
};

export { api, ApiError };
